import {
	businessActivityIcon,
	classicReportIcon,
	companyIcon,
	compareIcon,
} from "../../core/elements/icons/index.js";
import { iconAndText } from "../../core/elements/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import { none } from "../../core/elements/size/index.js";
import accountingDocument from "../shared/accountingDocument.js";
import AnnualTrend from "../../models/accounting/AnnualTrend.js";
import BusinessOverviewWidget from "./BusinessOverviewWidget.js";
import condensedStatementWidget from "../reports/periodReport/condensedStatementWidget/condensedStatementWidget.js";
import PeriodReportService from "../reports/periodReport/PeriodReportService.js";
import getKeyFigureService from "../../core/services/getKeyFigureService.js";
import keyFigureOverviewWidget from "./keyFigureOverviewWidget.js";
import KeyFigurePresetService from "../../core/services/KeyFigurePresetService.js";
import KeyFigureResultService from "../../core/services/KeyFigureResultService.js";
import ReportService from "../reports2/ReportService.js";
import ReportTemplate from "../../models/reports/templates/ReportTemplate.js";
import { _ } from "../../core/polyglot/polyglot.js";
import session from "../../core/session.js";

const condensedIncomeStatementReportId = "CondensedIncomeStatement";
const condensedBalanceSheetReportId = "CondensedBalanceSheet";

/**
 * Accounting's landing page that displays a dashboard with a business
 * overview, key figures, and condensed financial statement widgets.
 *
 *  _Options:_
 *
 * @param {object} spec - Object configuration
 * @param {object} my - Protected properties
 */
function overviewDocument(spec, my) {
	my ||= {};
	const that = accountingDocument(spec, my);

	that.title = _("Overview");

	that.documentId = "overview";

	that.help = function () {
		return [
			{
				title: _("Overview"),
				content: _(
					"This page will give an overview to how the business is performing and the general financial position of the company. The first graph visualizes monthly profits and losses and how the accumulated result is developing. Furthermore KPIs and condensed financial reports are shown to give the business owner a good overview of the current position of the company.",
				),
			},
			{
				title: _("Business"),
				content: _(
					"The yellow bars visualize monthly profits and losses and the green line represents the accumulated result. By clicking on the yellow bars you will activate that specific period.",
				),
				element: "business-chart",
			},
			{
				title: _("Links to reports"),
				content: _(
					"Click the links below the chart to jump to one of the reports.",
				),
				element: "overview-quicklinks",
			},
			{
				title: _("KPIs"),
				content: _(
					"Three KPIs are shown to give the user a better picture of how the company is doing.",
				),
				element: "kpi-overview",
			},
			{
				title: _("Profit and Loss"),
				content: _(
					"This widget shows the condensed profit and loss report for the selected period.",
				),
				element: "overview-income-statement",
			},
			{
				title: _("Balance Sheet"),
				content: _(
					"This widget shows the condensed balance sheet for the selected period.",
				),
				element: "overview-balance-sheet",
			},
		];
	};

	that.subtitle = function (html) {
		html.render(
			_("{company} - financial position at {period}", {
				company: session.getCompany().getName(),
				period: session.getPeriod().toSmartString(),
			}),
		);
	};

	const antiForgeryToken = session.getAntiForgeryToken();

	my.annualTrendRepository = makeMonitorRepository({
		route: "/Shared/Company/#companyId/FiscalYear/#fiscalYearId/AnnualTrend",
		defaultFilters: { companyId: session.getCompany().getLegacyId() },
		model: AnnualTrend,
		antiForgeryToken,
	});

	my.keyFigureResultService = new KeyFigureResultService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.keyFigurePresetService = new KeyFigurePresetService({
		companyId: session.getCompanyId(),
		keyFigureService: getKeyFigureService(),
	});

	my.condensedReportTemplateRepository = makeMonitorRepository({
		model: ReportTemplate,
		route: "/Shared/Country/#ownerId/?reportTemplateId",
		defaultFilters: {
			ownerId: session.getCountry().getId(),
		},
		antiForgeryToken,
	});

	my.businessOverview = new BusinessOverviewWidget({
		currentPeriod: session.getPeriod(),
	});

	my.keyFigureOverview = keyFigureOverviewWidget();

	my.incomeStatementOverview = condensedStatementWidget({
		cssClass: "condensed-income-statement",
	});

	my.balanceSheetOverview = condensedStatementWidget({
		cssClass: "condensed-balance-sheet",
	});

	my.periodReportService = new PeriodReportService();

	that.show = function () {
		that.update();
	};

	that.isActionsMenuMobileOnly = function () {
		return true;
	};

	that.showCurrentYear = function () {
		const period = session.getPeriod();

		return Promise.all([
			my.showBusinessOverview(period),
			my.showKeyFigureOverview(period),
			my.showIncomeStatementOverview(),
			my.showBalanceSheetOverview(),
		]);
	};

	my.showBusinessOverview = async function (period) {
		const annualTrendData = await my.annualTrendRepository.get({
			filter: { fiscalYearId: session.getFiscalYearId() },
		});

		my.businessOverview.show(annualTrendData, period);
	};

	my.showKeyFigureOverview = async function (period) {
		const preset = await my.keyFigurePresetService.get("Overview");

		const keyFigureValues =
			await my.keyFigureResultService.getValuesForPeriods({
				periods: [period],
				keyFigures: preset.getKeyFigures(),
			});

		my.keyFigureOverview.show(
			keyFigureValues.getValuesOrderedByKeyFigure(),
		);
	};

	my.showIncomeStatementOverview = async function () {
		const incomeStatement = await my.fetchCondensedReport({
			reportTemplateId: condensedIncomeStatementReportId,
		});

		my.incomeStatementOverview.setReport(incomeStatement);
	};

	my.showBalanceSheetOverview = async function () {
		const balanceSheet = await my.fetchCondensedReport({
			reportTemplateId: condensedBalanceSheetReportId,
		});

		my.balanceSheetOverview.setReport(balanceSheet);
	};

	my.fetchCondensedReport = async function (params) {
		const { reportTemplateId } = params;

		const reportTemplate = await my.condensedReportTemplateRepository.get({
			filter: { reportTemplateId },
		});

		const reportService = new ReportService({
			period: session.getPeriod(),
			reportTemplate,
			company: session.getCompany(),
			chartOfAccountsId: session.getCurrentChartOfAccountsId(),
		});

		await reportService.fetchChartOfAccountsAndKeyFigures();
		await reportService.updateReportAndData();

		return reportService.getReport();
	};

	my.renderContentOn = function (html) {
		my.renderTopLinksOn(html);
		my.renderBusinessOverviewOn(html);
		my.renderKeyFiguresOn(html);
		my.renderFinancialStatementsOn(html);
	};

	my.renderBusinessOverviewOn = function (html) {
		html.render(
			my.section({
				id: "business-chart",
				cssClass: "business-chart small",
				dataHelpName: "business-chart",
				title: _("Business"),
				content: my.businessOverview,
			}),
		);
	};

	my.renderKeyFiguresOn = function (html) {
		html.render(
			my.section({
				id: "kpi-overview",
				cssClass: "small",
				dataHelpName: "kpi-overview",
				title: _("KPIs"),
				content: my.keyFigureOverview,
			}),
		);
	};

	my.renderTopLinksOn = function (html) {
		const canCreatePeriodReports = session
			.getCurrentUser()
			.canCreatePeriodReports(session.getCompany());

		const topLinks = [
			html.a(
				{
					href: my.linkTo("company", {}),
					class: "overview-top-link",
				},
				iconAndText({ icon: companyIcon }, _("The Company")),
			),
			html.a(
				{
					href: my.linkTo("business-trend", {}),
					class: "overview-top-link",
				},
				iconAndText({ icon: compareIcon }, _("Business trend")),
			),
			html.a(
				{
					href: my.linkTo("business-activity", {}),
					class: "overview-top-link",
				},
				iconAndText(
					{ icon: businessActivityIcon },
					_("Business activity"),
				),
			),
		];

		if (canCreatePeriodReports) {
			topLinks.push(
				html.a(
					{
						href: "#",
						class: "overview-top-link",
						click: async (event) => {
							event.preventDefault();
							await my.createDraftPeriodReportAndRedirect();
						},
					},
					iconAndText(
						{ icon: classicReportIcon },
						_("Create period report"),
					),
				),
			);
		}

		html.div(
			{ class: "overview-top-links", "data-help": "overview-quicklinks" },
			topLinks,
		);
	};

	my.createDraftPeriodReportAndRedirect = async function () {
		that.showUpdating();

		const draft = await my.periodReportService.createDraft();

		my.redirectTo("edit-period-report", {
			id: draft.getId(),
		});
	};

	my.renderFinancialStatementsOn = function (html) {
		html.div(
			{ class: "overview-financial-reports" },
			my.section({
				cssClass: "overview-financial-report",
				id: "overview-income-statement",
				dataHelpName: "overview-income-statement",
				title: _("Income statement"),
				content: [
					my.incomeStatementOverview,
					my.renderIncomeStatementQuickLinkOn,
				],
			}),
			my.section({
				cssClass: "overview-financial-report",
				id: "overview-balance-sheet",
				dataHelpName: "overview-balance-sheet",
				title: _("Balance sheet"),
				content: [
					my.balanceSheetOverview,
					my.renderBalanceSheetQuickLinkOn,
				],
			}),
		);
	};

	my.renderIncomeStatementQuickLinkOn = function (html) {
		my.renderReportQuickLinkOn({
			html,
			linkTo: "income-statement",
			label: _("Income statement"),
		});
	};

	my.renderBalanceSheetQuickLinkOn = function (html) {
		my.renderReportQuickLinkOn({
			html,
			linkTo: "balance-sheet",
			label: _("Balance sheet"),
		});
	};

	my.renderReportQuickLinkOn = function ({ html, linkTo, label }) {
		html.div(
			{ class: "overview-quicklinks report" },
			html.ul(
				html.li(
					{ class: "classic" },
					html.a(
						{
							class: "overview-financial-report-link",
							href: my.linkTo(linkTo, {
								period: my.getReportLinkPeriod().toString(),
							}),
						},
						iconAndText(
							{ space: none, icon: classicReportIcon },
							label,
						),
					),
				),
			),
		);
	};

	my.getReportLinkPeriod = function () {
		const period = session.getPeriod();

		if (period.isConsecutiveMonths()) {
			return period.endMonth();
		}

		return period;
	};

	return that;
}

export default overviewDocument;
